import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  inject
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';

import { ApiService } from '@core/api/api.service';
import { SavedFieldDirective } from '@shared/directives/saved-field.directive';
import { KgQuantityComponent } from '@shared/components/kg-quantity/kg-quantity.component';
import { cartItemCount, lineTotal } from '@core/weight-quantity';
import { AuthService } from '@core/auth/auth.service';
import {
  BasketNeed,
  ProductAlternativeMatch,
  ProductBranchPrice,
  ShoppingBasket
} from '@core/models/models';

@Component({
  selector: 'app-basket-view',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, SavedFieldDirective, KgQuantityComponent],
  templateUrl: './basket-view.component.html',
  styleUrl: './basket-view.component.css',
  changeDetection: ChangeDetectionStrategy.Default
})
export class BasketViewComponent {
  private readonly api = inject(ApiService);
  readonly auth = inject(AuthService);

  @Input() basket: ShoppingBasket | null = null;
  @Input() selectedBranchId: number | null = null;
  @Input() selectedCityLabel = '';
  @Input() selectedBranchLabel = '';
  @Input() hasSelectedLocation = false;
  @Input() basketLoading = false;
  @Input() isBasketClosed = false;
  @Input() busyNeedIds = new Set<number>();
  @Input() estimatingNeedIds = new Set<number>();
  @Input() swappingNeedIds = new Set<number>();
  @Input() loadingAlternatives = new Set<number>();
  @Input() alternativesByProduct = new Map<number, ProductAlternativeMatch[]>();
  @Input() catalogPrices = new Map<number, ProductBranchPrice>();
  @Input() countBumped = false;
  @Input() basketOriginBranchMismatch = false;
  @Input() basketOriginBranchLabel = '';
  @Input() productIdsWithImages = new Set<number>();
  @Input() brokenImageIds = new Set<number>();
  @Input() savingBasketName = false;
  @Input() basketNameSaved = 0;

  @Output() quantityChange = new EventEmitter<{ need: BasketNeed; quantity: number }>();
  @Output() weightPhoto = new EventEmitter<{ need: BasketNeed; frameJpeg: string }>();
  @Output() removeNeed = new EventEmitter<BasketNeed>();
  @Output() swapWithAlternative = new EventEmitter<{ need: BasketNeed; alt: ProductAlternativeMatch }>();
  @Output() saveBasketName = new EventEmitter<string>();
  @Output() reopenBasket = new EventEmitter<void>();
  @Output() toggleShare = new EventEmitter<void>();
  @Output() switchBranch = new EventEmitter<void>();
  @Output() openLocation = new EventEmitter<void>();
  @Output() openLogin = new EventEmitter<void>();
  @Output() openImage = new EventEmitter<number>();
  @Output() imageBroken = new EventEmitter<number>();

  isRenamingBasket = false;
  renameBasketText = '';
  imagePopupProductId: number | null = null;

  openImagePopup(productId: number): void {
    this.imagePopupProductId = productId;
    this.openImage.emit(productId);
  }

  closeImagePopup(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.imagePopupProductId = null;
  }

  startRenamingBasket(): void {
    if (!this.basket || this.isBasketClosed) return;
    this.renameBasketText = this.basket.name || '';
    this.isRenamingBasket = true;
  }

  cancelRenamingBasket(): void {
    this.isRenamingBasket = false;
    this.renameBasketText = '';
  }

  onSaveBasketName(): void {
    if (!this.basket || this.savingBasketName) return;
    const trimmed = this.renameBasketText.trim();
    if (!trimmed) {
      this.cancelRenamingBasket();
      return;
    }
    if (trimmed === this.basket.name) {
      this.isRenamingBasket = false;
      return;
    }
    this.saveBasketName.emit(trimmed);
    this.isRenamingBasket = false;
  }

  get totalCartItemsCount(): number {
    if (!this.basket?.needs || this.basket.needs.length === 0) {
      return 0;
    }
    return cartItemCount(this.basket.needs);
  }

  hasProductPhoto(productId: number | null | undefined): boolean {
    return (
      productId != null &&
      this.productIdsWithImages.has(productId) &&
      !this.brokenImageIds.has(productId)
    );
  }

  productImageUrl(productId: number): string {
    return this.api.productImageUrl(productId);
  }

  markImageBroken(productId: number): void {
    this.brokenImageIds.add(productId);
    this.imageBroken.emit(productId);
  }

  needCatalogPrice(need: BasketNeed): ProductBranchPrice | null {
    if (need.lockedProductId == null) return null;
    return this.catalogPrices.get(need.lockedProductId) ?? null;
  }

  needPayPrice(need: BasketNeed): number | null {
    const price = this.needCatalogPrice(need);
    if (!price) return null;
    return price.promoPrice ?? price.price;
  }

  needLineTotal(need: BasketNeed): number | null {
    const pay = this.needPayPrice(need);
    if (pay == null) return null;
    return lineTotal(pay, need.quantity, !!need.soldByWeight, this.needCatalogPrice(need)?.unitOfMeasure);
  }

  listTotal(): number | null {
    if (!this.basket || this.basket.needs.length === 0) return null;
    let total = 0;
    let priced = 0;
    for (const need of this.basket.needs) {
      const line = this.needLineTotal(need);
      if (line == null) continue;
      total += line;
      priced += 1;
    }
    return priced === 0 ? null : total;
  }

  listPricedCount(): number {
    if (!this.basket) return 0;
    return this.basket.needs.filter((need) => this.needPayPrice(need) != null).length;
  }

  listUnpricedCount(): number {
    if (!this.basket) return 0;
    return this.basket.needs.filter(
      (need) => need.lockedProductId != null && this.needPayPrice(need) == null
    ).length;
  }

  listCurrency(): string {
    return 'ILS';
  }

  formatMoney(value: number | null, currency: string): string {
    if (value == null) return 'אין מחיר';
    try {
      return new Intl.NumberFormat('he-IL', {
        style: 'currency',
        currency,
        maximumFractionDigits: 2
      }).format(value);
    } catch {
      return `${value.toFixed(2)} ${currency}`;
    }
  }

  formatNeedQuantity(quantity: number): string {
    return Number.isInteger(quantity)
      ? String(quantity)
      : quantity.toLocaleString('he-IL', { maximumFractionDigits: 2 });
  }

  trackNeed(_: number, need: BasketNeed): number {
    return need.id;
  }
}
